import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { 
  Download, 
  Printer, 
  FileText,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle2,
  AlertCircle,
  Target,
  Award,
  Users,
  Sparkles,
  Heart,
  Building2,
  Calendar,
  Zap,
  MessageSquare,
  Timer,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Star,
  Shield
} from "lucide-react";
import { OuvidoriaRelatorioMensal, MESES_NOMES } from "@/types/relatorio";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

interface RelatorioVisualModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  relatorio: OuvidoriaRelatorioMensal | null;
  tipo: 'mensal' | 'anual';
  municipioNome?: string;
  ouvidoriaNome?: string;
}

function MetricaCard({ 
  titulo, 
  valor, 
  unidade, 
  icon: Icon, 
  trend, 
  destaque,
  subtexto,
  cor
}: { 
  titulo: string; 
  valor: string | number; 
  unidade?: string;
  icon: React.ElementType;
  trend?: 'up' | 'down' | null;
  destaque?: boolean;
  subtexto?: string;
  cor?: 'success' | 'warning' | 'destructive' | 'info';
}) {
  const getCorClasses = () => {
    if (destaque) return "bg-gradient-to-br from-primary via-primary to-primary-dark text-primary-foreground shadow-lg shadow-primary/25";
    switch (cor) {
      case 'success': return "bg-success/10 border-success/20 text-success";
      case 'warning': return "bg-warning/10 border-warning/20 text-warning";
      case 'destructive': return "bg-destructive/10 border-destructive/20 text-destructive";
      case 'info': return "bg-info/10 border-info/20 text-info";
      default: return "bg-surface-container border border-border/50";
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "relative overflow-hidden rounded-2xl p-5 transition-all",
        getCorClasses()
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className={cn(
            "text-xs font-medium uppercase tracking-wider mb-2",
            destaque ? "text-primary-foreground/80" : "text-muted-foreground"
          )}>
            {titulo}
          </p>
          <div className="flex items-baseline gap-1.5">
            <span className={cn(
              "text-3xl font-heading font-bold tracking-tight",
              destaque ? "text-primary-foreground" : "text-foreground"
            )}>
              {valor}
            </span>
            {unidade && (
              <span className={cn(
                "text-lg font-medium",
                destaque ? "text-primary-foreground/70" : "text-muted-foreground"
              )}>
                {unidade}
              </span>
            )}
          </div>
          {subtexto && (
            <p className={cn(
              "text-xs mt-1.5",
              destaque ? "text-primary-foreground/70" : "text-muted-foreground"
            )}>
              {subtexto}
            </p>
          )}
        </div>
        <div className={cn(
          "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0",
          destaque ? "bg-white/20" : cor ? `bg-${cor}/10` : "bg-primary/10"
        )}>
          <Icon className={cn("h-6 w-6", destaque ? "text-primary-foreground" : cor ? `text-${cor}` : "text-primary")} />
        </div>
      </div>
      {trend && (
        <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-white/10">
          {trend === 'up' ? (
            <>
              <ArrowUpRight className="h-4 w-4 text-success" />
              <span className="text-xs text-success font-medium">Acima da meta</span>
            </>
          ) : (
            <>
              <ArrowDownRight className="h-4 w-4 text-destructive" />
              <span className="text-xs text-destructive font-medium">Abaixo do esperado</span>
            </>
          )}
        </div>
      )}
    </motion.div>
  );
}

function SLAProgressBar({ 
  titulo, 
  valor, 
  meta = 90 
}: { 
  titulo: string; 
  valor: number | null;
  meta?: number;
}) {
  const valorExibido = valor ?? 0;
  const atingiuMeta = valorExibido >= meta;
  
  return (
    <div className="bg-surface-container rounded-2xl p-5 border border-border/50">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {atingiuMeta ? (
            <CheckCircle2 className="h-4 w-4 text-success" />
          ) : (
            <AlertCircle className="h-4 w-4 text-warning" />
          )}
          <p className="text-sm font-medium text-foreground">{titulo}</p>
        </div>
        <Badge variant={atingiuMeta ? "default" : "secondary"} className={cn(
          "font-heading font-bold",
          atingiuMeta && "bg-success text-success-foreground"
        )}>
          {valorExibido.toFixed(1)}%
        </Badge>
      </div>
      <div className="relative">
        <Progress 
          value={valorExibido} 
          className="h-3"
        />
        {/* Linha da meta */}
        <div 
          className="absolute top-0 bottom-0 w-0.5 bg-foreground/30"
          style={{ left: `${meta}%` }}
        />
      </div>
      <div className="flex justify-between mt-2">
        <span className="text-xs text-muted-foreground">0%</span>
        <span className="text-xs text-muted-foreground">Meta: {meta}%</span>
        <span className="text-xs text-muted-foreground">100%</span>
      </div>
    </div>
  );
}

function FraseMotivacional({ tipo, percentualSLA }: { tipo: 'mensal' | 'anual'; percentualSLA?: number | null }) {
  const frases = {
    excelente: [
      "🏆 Parabéns! Sua ouvidoria está transformando a cidade através da escuta ativa dos cidadãos. Cada solicitação respondida é um passo para uma comunidade mais unida e participativa.",
      "🌟 Excelência em atendimento! O compromisso da sua equipe com a população é inspirador. Vocês representam o melhor da gestão pública moderna.",
      "💚 Juntos, estamos construindo uma cidade melhor! A dedicação em ouvir e responder aos cidadãos fortalece a confiança na administração pública."
    ],
    bom: [
      "👏 Ótimo trabalho! Sua dedicação em ouvir o cidadão faz toda a diferença na gestão pública. Continue assim e os resultados serão ainda melhores!",
      "🚀 A participação cidadã fortalece nossa democracia. Cada resposta dada representa um laço de confiança com a população.",
      "💪 Cada solicitação atendida é uma vida impactada positivamente. O trabalho da sua equipe transforma a realidade local!"
    ],
    regular: [
      "📈 Há espaço para crescer, mas cada passo conta. A jornada pela excelência é contínua e vocês estão no caminho certo.",
      "🎯 Foco no cidadão! Com dedicação e ajustes nos processos, os resultados virão. A população conta com vocês.",
      "💡 Identificamos oportunidades de melhoria. Juntos, podemos elevar ainda mais o nível de atendimento à comunidade!"
    ]
  };

  const categoria = percentualSLA 
    ? (percentualSLA >= 90 ? 'excelente' : percentualSLA >= 75 ? 'bom' : 'regular')
    : 'bom';

  const frasesCategoria = frases[categoria];
  const fraseAleatoria = frasesCategoria[Math.floor(Math.random() * frasesCategoria.length)];

  const getGradient = () => {
    switch (categoria) {
      case 'excelente': return 'from-success/10 via-primary/5 to-success/10 border-success/20';
      case 'bom': return 'from-primary/10 via-secondary/5 to-primary/10 border-primary/20';
      default: return 'from-warning/10 via-muted/5 to-warning/10 border-warning/20';
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.2 }}
      className={cn(
        "relative overflow-hidden rounded-2xl p-6 border",
        `bg-gradient-to-r ${getGradient()}`
      )}
    >
      <div className="absolute -top-16 -right-16 w-48 h-48 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-secondary/5 rounded-full blur-3xl" />
      
      <div className="relative flex items-start gap-4">
        <div className={cn(
          "w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg",
          categoria === 'excelente' 
            ? "bg-gradient-to-br from-success to-primary" 
            : categoria === 'bom'
              ? "bg-gradient-to-br from-primary to-secondary"
              : "bg-gradient-to-br from-warning to-tertiary"
        )}>
          {categoria === 'excelente' ? (
            <Star className="h-7 w-7 text-white" />
          ) : categoria === 'bom' ? (
            <Sparkles className="h-7 w-7 text-white" />
          ) : (
            <Target className="h-7 w-7 text-white" />
          )}
        </div>
        <div className="flex-1">
          <h4 className="font-heading font-bold text-lg text-foreground mb-2">
            Mensagem {tipo === 'anual' ? 'do Ano' : 'do Mês'}
          </h4>
          <p className="text-foreground/80 leading-relaxed text-sm">
            {fraseAleatoria}
          </p>
          <div className="mt-4 pt-4 border-t border-border/50">
            <p className="text-xs text-muted-foreground italic flex items-center gap-2">
              <Heart className="h-3.5 w-3.5 text-destructive" />
              "A participação cidadã é o pilar de uma gestão pública transparente e eficiente."
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function RelatorioVisualModal({ 
  open, 
  onOpenChange, 
  relatorio, 
  tipo,
  municipioNome = "Município",
  ouvidoriaNome = "Ouvidoria"
}: RelatorioVisualModalProps) {
  if (!relatorio) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl">
          <div className="p-8 text-center text-muted-foreground">Carregando relatório...</div>
        </DialogContent>
      </Dialog>
    );
  }

  const titulo = tipo === 'mensal'
    ? `${MESES_NOMES[relatorio.mes - 1]} de ${relatorio.ano}`
    : `Consolidado ${relatorio.ano}`;

  const taxaResolucao = relatorio.total_solicitacoes > 0
    ? ((relatorio.total_concluidas / relatorio.total_solicitacoes) * 100).toFixed(1)
    : '0';

  const formatarHoras = (horas: number | null) => {
    if (horas === null) return '--';
    if (horas < 1) return `${Math.round(horas * 60)}min`;
    if (horas < 24) return `${horas.toFixed(1)}h`;
    const dias = Math.floor(horas / 24);
    const horasRestantes = Math.round(horas % 24);
    return `${dias}d ${horasRestantes}h`;
  };

  const formatarHorasDetalhado = (horas: number | null) => {
    if (horas === null) return { valor: '--', unidade: '' };
    if (horas < 1) return { valor: Math.round(horas * 60).toString(), unidade: 'min' };
    if (horas < 24) return { valor: horas.toFixed(1), unidade: 'h' };
    const dias = Math.floor(horas / 24);
    return { valor: dias.toString(), unidade: dias === 1 ? 'dia' : 'dias' };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto p-0">
        {/* Header institucional */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-primary via-primary-dark to-secondary text-primary-foreground p-8">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImEiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+PHBhdGggZD0iTTAgMGgyMHYyMEgweiIgZmlsbD0ibm9uZSIvPjxjaXJjbGUgY3g9IjEwIiBjeT0iMTAiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgZmlsbD0idXJsKCNhKSIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIvPjwvc3ZnPg==')] opacity-30" />
          
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
                <Building2 className="h-8 w-8" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-heading font-bold text-primary-foreground">
                  Relatório {tipo === 'anual' ? 'Anual' : 'Mensal'}
                </DialogTitle>
                <p className="text-primary-foreground/80 text-sm mt-1 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  {ouvidoriaNome} • {municipioNome}
                </p>
              </div>
            </div>
            <Badge variant="secondary" className="bg-white/20 text-white border-0 text-sm px-4 py-2 font-heading font-semibold">
              <Calendar className="h-4 w-4 mr-2" />
              {titulo}
            </Badge>
          </div>
          
          {/* Curva decorativa */}
          <div className="absolute bottom-0 left-0 right-0 h-6 bg-background rounded-t-[2rem]" />
        </div>

        <div className="p-8 space-y-8">
          {/* Frase motivacional */}
          <FraseMotivacional 
            tipo={tipo} 
            percentualSLA={relatorio.percentual_resolucao_dentro_prazo} 
          />

          {/* Métricas principais em destaque */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Target className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-heading font-bold text-xl text-foreground">
                Indicadores Principais
              </h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricaCard
                titulo="Total de Solicitações"
                valor={relatorio.total_solicitacoes}
                icon={FileText}
                destaque
                subtexto="Recebidas no período"
              />
              <MetricaCard
                titulo="Resolvidas"
                valor={relatorio.total_concluidas}
                icon={CheckCircle2}
                cor="success"
                trend={Number(taxaResolucao) >= 85 ? 'up' : undefined}
                subtexto={`${taxaResolucao}% do total`}
              />
              <MetricaCard
                titulo="Pendentes"
                valor={relatorio.total_pendentes}
                icon={AlertCircle}
                cor={relatorio.total_pendentes > 50 ? 'warning' : undefined}
                subtexto="Aguardando resolução"
              />
              <MetricaCard
                titulo="Taxa de Resolução"
                valor={taxaResolucao}
                unidade="%"
                icon={Award}
                trend={Number(taxaResolucao) >= 85 ? 'up' : 'down'}
                cor={Number(taxaResolucao) >= 85 ? 'success' : Number(taxaResolucao) >= 70 ? 'warning' : 'destructive'}
              />
            </div>
          </div>

          <Separator className="my-8" />

          {/* Métricas de tempo */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-secondary" />
              </div>
              <h3 className="font-heading font-bold text-xl text-foreground">
                Tempos de Resposta
              </h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <MetricaCard
                titulo="Primeira Resposta"
                valor={formatarHorasDetalhado(relatorio.media_tempo_primeira_resposta_horas).valor}
                unidade={formatarHorasDetalhado(relatorio.media_tempo_primeira_resposta_horas).unidade}
                icon={Zap}
                subtexto="Tempo médio"
              />
              <MetricaCard
                titulo="Encaminhamento"
                valor={formatarHorasDetalhado(relatorio.media_tempo_encaminhamento_horas).valor}
                unidade={formatarHorasDetalhado(relatorio.media_tempo_encaminhamento_horas).unidade}
                icon={TrendingUp}
                subtexto="Para setor responsável"
              />
              <MetricaCard
                titulo="Resolução Média"
                valor={formatarHorasDetalhado(relatorio.media_tempo_resolucao_horas).valor}
                unidade={formatarHorasDetalhado(relatorio.media_tempo_resolucao_horas).unidade}
                icon={CheckCircle2}
                destaque
                subtexto="Do início ao fim"
              />
              <MetricaCard
                titulo="Maior Resolução"
                valor={formatarHorasDetalhado(relatorio.maior_tempo_resolucao_horas).valor}
                unidade={formatarHorasDetalhado(relatorio.maior_tempo_resolucao_horas).unidade}
                icon={Timer}
                cor="warning"
                subtexto="Caso mais demorado"
              />
            </div>
          </div>

          <Separator className="my-8" />

          {/* Interações e Acompanhamento */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-tertiary/10 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-tertiary" />
              </div>
              <h3 className="font-heading font-bold text-xl text-foreground">
                Qualidade do Atendimento
              </h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <MetricaCard
                titulo="Atualizações por Solicitação"
                valor={relatorio.media_atualizacoes_por_solicitacao?.toFixed(1) || '--'}
                icon={MessageSquare}
                subtexto="Média de interações"
              />
              <MetricaCard
                titulo="Intervalo entre Atualizações"
                valor={formatarHorasDetalhado(relatorio.media_intervalo_atualizacoes_horas).valor}
                unidade={formatarHorasDetalhado(relatorio.media_intervalo_atualizacoes_horas).unidade}
                icon={Clock}
                subtexto="Tempo médio"
              />
              <MetricaCard
                titulo="Maior Tempo 1ª Resposta"
                valor={formatarHorasDetalhado(relatorio.maior_tempo_primeira_resposta_horas).valor}
                unidade={formatarHorasDetalhado(relatorio.maior_tempo_primeira_resposta_horas).unidade}
                icon={AlertCircle}
                cor="warning"
                subtexto="Pior caso registrado"
              />
            </div>
          </div>

          <Separator className="my-8" />

          {/* SLA e Metas */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
                <Award className="h-5 w-5 text-success" />
              </div>
              <h3 className="font-heading font-bold text-xl text-foreground">
                Cumprimento de Metas (SLA)
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SLAProgressBar
                titulo="Primeira Resposta dentro do Prazo"
                valor={relatorio.percentual_primeira_resposta_dentro_prazo}
                meta={90}
              />
              <SLAProgressBar
                titulo="Resolução dentro do Prazo"
                valor={relatorio.percentual_resolucao_dentro_prazo}
                meta={85}
              />
            </div>
          </div>

          <Separator className="my-8" />

          {/* Rodapé institucional */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="bg-gradient-to-br from-muted/50 via-muted/30 to-muted/50 rounded-2xl p-8 text-center border border-border/50"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Heart className="h-6 w-6 text-white" />
              </div>
            </div>
            <h4 className="font-heading font-bold text-xl text-foreground mb-2">
              Agradecemos seu compromisso com a população!
            </h4>
            <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Cada solicitação atendida representa um cidadão ouvido e uma cidade mais participativa. 
              O trabalho diário da sua equipe transforma vidas e constrói uma gestão pública mais 
              transparente e eficiente. Continue esse trabalho extraordinário!
            </p>
            <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-border/50">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="h-4 w-4" />
                <span>Cidadãos atendidos</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <BarChart3 className="h-4 w-4" />
                <span>Gestão baseada em dados</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="h-4 w-4" />
                <span>Transparência pública</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground/70 mt-6">
              Relatório gerado automaticamente em {new Date(relatorio.gerado_em).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })} • UrbNet - Sistema de Gestão de Ouvidoria
            </p>
          </motion.div>

          {/* Ações */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Relatório {tipo === 'mensal' ? 'mensal' : 'anual'} • {ouvidoriaNome}
            </p>
            <div className="flex gap-3">
              <Button variant="outline" size="lg" className="gap-2">
                <Printer className="h-4 w-4" />
                Imprimir
              </Button>
              <Button size="lg" className="gap-2">
                <Download className="h-4 w-4" />
                Baixar PDF
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
